import { useMutation } from "@apollo/client/react";
import { object } from "yup";
import { Form, Formik } from "formik";
import type { ErrorLike } from "@apollo/client/core";
import { gql } from "graphql-tag";
import { useCallback } from "react";
import { RequestCodeViaTextDocument } from "./generated";
import { captureException } from "@sentry/nextjs";
import { FormInputPhoneNumber } from "@portal/app/_components/ui-elements/FormInputs";
import { ValidatedFormikField } from "@portal/app/_components/ui-elements/formHelpers";
import { LOCAL_STORAGE_KEYS, useLocalStorage } from "@portal/app/_components/hooks/useLocalStorage";
import Box from "@mui/material/Box";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/pro-solid-svg-icons/faSpinner";
import { yupRules } from "@portal/app/_common/lib/yupValidationRules";
import { logger } from "@portal/app/_common/lib/logger";
import { SIGN_IN_METHOD, useSignInContext } from "@portal/app/_components/flow/steps/SignInStep";
import Button from "@mui/material/Button";

void gql`
  mutation requestCodeViaText($phone: String!) {
    requestCodeViaPhone(phone: $phone, isMobile: true)
  }
`;

type FormValues = { phone: string };
export const Text = () => {
  const { setPage, setClientErrorMessage } = useSignInContext();
  const [requestCodeViaText, { loading }] = useMutation(RequestCodeViaTextDocument);
  const [, setMethod, removeMethod] = useLocalStorage<string>(LOCAL_STORAGE_KEYS.MAGIC_LINK_METHOD);
  const [, setPhone, removePhone] = useLocalStorage<string>(LOCAL_STORAGE_KEYS.MAGIC_LINK_PHONE);
  const [, , removeEmail] = useLocalStorage<string>(LOCAL_STORAGE_KEYS.MAGIC_LINK_EMAIL);
  const [, setCodeSent] = useLocalStorage<boolean>(LOCAL_STORAGE_KEYS.MAGIC_LINK_CODE_SENT);

  const onSubmit = useCallback(
    ({ phone }: FormValues) => {
      setClientErrorMessage(null);
      setPhone(phone);
      setMethod(SIGN_IN_METHOD.TEXT);
      removeEmail();

      return requestCodeViaText({
        variables: {
          phone,
        },
      })
        .then(() => {
          setCodeSent(true);
          setPage("CODE");
        })
        .catch((error: ErrorLike) => {
          setClientErrorMessage({
            title: "We couldn't text that number",
            description: 'Please select "Use my email", or log in with your Google account.',
          });
          captureException(error);
          logger.error(error);
          removeMethod();
          removePhone();
        });
    },
    [
      setClientErrorMessage,
      setPhone,
      setMethod,
      removeEmail,
      requestCodeViaText,
      setCodeSent,
      setPage,
      removeMethod,
      removePhone,
    ]
  );

  return (
    <Formik<FormValues>
      initialValues={{ phone: "" }}
      onSubmit={async (values, { resetForm }) => {
        await onSubmit(values);
        resetForm();
      }}
      validationSchema={object().shape({
        phone: yupRules.required.phone,
      })}
    >
      {({ errors }) => (
        <Form noValidate data-testid="text-form">
          <Box sx={{ mt: 2 }}>
            <ValidatedFormikField
              as={FormInputPhoneNumber}
              name="phone"
              aria-label="Phone number"
              disabled={loading}
              data-hj-allow={true}
              autoFocus
            />
            <Button
              type="submit"
              data-testid="submit"
              fullWidth
              sx={{ mt: 2 }}
              disabled={loading || !!errors.phone}
            >
              {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : "Continue"}
            </Button>
          </Box>
        </Form>
      )}
    </Formik>
  );
};
